<script setup lang="ts">
import {onMounted, PropType, Ref, ref, watch} from 'vue'
import api from "@/app/services/api.ts";
import type {AxiosResponse} from "axios";
import type {ApiResponse} from "@/app/types";
import {IPostalAddress} from "@/domains/home/services/nir/forms/components/widgets/types";

const props = defineProps({
  data: {
    type: Object as PropType<IPostalAddress>,
  },
})


interface Station {
  id: string
  name: string
  description: string
}

const stations:Ref<Station[]> = ref([])
const selectedStation = ref<Station | undefined>()

onMounted(() => {
  fetchStations()
})

const fetchStations = async () => {
  return api
      .get(`/integration/api/eposta/stations`)
      .then((response: AxiosResponse<ApiResponse<Station[]>>) => {
        stations.value = response.data.data
        // Once stations are fetched, find the selected station
        if (props.data?.stationId) {
          selectedStation.value = stations.value.find(s => s.id === props.data?.stationId)
        }
      })
}

// Watch for data prop changes
watch(() => props.data, (newValue) => {
  if (newValue?.stationId && stations.value.length > 0) {
    selectedStation.value = stations.value.find(s => s.id === newValue.stationId)
  }
}, { deep: true })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="data && (data.stationId || data.number)" class="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
      <!-- Station/Branch Information -->
      <div v-if="data.stationId" class="flex items-start">
        <i class="fa fa-building text-primary text-lg mr-3 mt-1"></i>
        <div class="flex-1">
          <p class="text-xs text-gray-500 uppercase tracking-wide">Post Office Station</p>
          <p class="text-base font-semibold text-gray-900">
            {{ selectedStation?.name || 'Loading...' }}
          </p>
          <p v-if="selectedStation?.description" class="text-xs text-gray-600 mt-1">
            {{ selectedStation.description }}
          </p>
        </div>
      </div>

      <!-- Postal Address Number -->
      <div v-if="data.number" class="flex items-start">
        <i class="fa fa-envelope text-primary text-lg mr-3 mt-1"></i>
        <div class="flex-1">
          <p class="text-xs text-gray-500 uppercase tracking-wide">P.O. Box Number</p>
          <p class="text-base font-semibold text-gray-900">{{ data.number }}</p>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-8 text-gray-500 text-sm">
      <i class="fa fa-inbox text-3xl mb-2"></i>
      <p>No postal address information available</p>
    </div>
  </div>
</template>

<style scoped></style>
